import { useVirtualizer } from '@tanstack/react-virtual';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { PagedItems } from './paged-items';

export interface VirtualGridProps<T> {
  items: PagedItems<T>;
  /** Minimum cell width; columns are derived from the container width. */
  minCellWidth: number;
  /** Cell height as a ratio of its width (posters are 1.5). */
  aspect: number;
  /** Extra height below the image area (title line). */
  captionHeight: number;
  gap: number;
  renderCell: (item: T | undefined, index: number) => ReactNode;
  className?: string;
}

/** Poster grid that virtualizes rows and fetches the pages they cover. */
export function VirtualGrid<T>({ items, minCellWidth, aspect, captionHeight, gap, renderCell, className }: VirtualGridProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = parentRef.current;
    if (!element) return undefined;
    const update = () => setWidth(element.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const columns = Math.max(1, Math.floor((width + gap) / (minCellWidth + gap)));
  const cellWidth = width > 0 ? (width - gap * (columns - 1)) / columns : minCellWidth;
  const rowHeight = Math.round(cellWidth * aspect + captionHeight + gap);
  const total = items.total ?? 0;
  const virtualizer = useVirtualizer({
    count: Math.ceil(total / columns),
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 3,
  });
  useEffect(() => virtualizer.measure(), [virtualizer, rowHeight]);

  const rows = virtualizer.getVirtualItems();
  const first = (rows[0]?.index ?? 0) * columns;
  const last = ((rows[rows.length - 1]?.index ?? 0) + 1) * columns - 1;
  useEffect(() => items.ensure(first, last), [items, first, last]);

  return (
    <div ref={parentRef} className={`virtual-scroll ${className ?? ''}`}>
      <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
        {rows.map((row) => (
          <div key={row.key} style={{ position: 'absolute', top: 0, left: 0, right: 0, height: rowHeight, transform: `translateY(${row.start}px)` }}>
            {Array.from({ length: columns }, (_, column) => {
              const index = row.index * columns + column;
              if (index >= total) return null;
              return (
                <div key={column} className="virtual-cell" style={{ position: 'absolute', top: 0, left: column * (cellWidth + gap), width: cellWidth, height: rowHeight - gap }}>
                  {renderCell(items.get(index), index)}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
